<template>
    <div class="content-wrapper">
        <section class="content-header">
            <h1>
                {{ $t('listCategory.tag.tags') }}

                <div class="pull-right">
                    <router-link
                        :to="{ name: 'contacts-lists.categories.tags.create' }"
                        class="btn btn-sm btn-primary"
                    >
                        <span class="sr-only">{{ $t('addNew') }}</span>
                        <span class="hidden-xs">{{ $t('addNew') }}</span>
                        <span class="visible-xs"><i class="fa fa-plus"></i></span>
                    </router-link>
                </div>
            </h1>
        </section>

        <section class="content">
            <Alerts />

            <div class="row">
                <div class="col-xs-12">
                    <div class="box box-primary">
                        <div
                            v-if="!isLoaded"
                            class="box-body"
                        >
                            <img
                                class="table-row-preloader"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                                alt="preload"
                            />
                        </div>

                        <template v-else-if="tags.length">
                            <div class="box-header with-border">
                                <h3 class="box-title">
                                    {{ $t('listCategory.tag.tagsTable') }}
                                </h3>
                                <div class="box-tools pull-right">
                                    <button
                                        :title="$t('collapse')"
                                        class="btn btn-box-tool"
                                        type="button"
                                        @click="isCollapse = !isCollapse"
                                    >
                                        <i
                                            :class="{
                                                'fa-plus': !isCollapse,
                                                'fa-minus': isCollapse,
                                            }"
                                            class="fa"
                                        />
                                    </button>
                                </div>
                            </div>

                            <vue-slide-up-down :active="isCollapse">
                                <div
                                    v-if="paginationOptions.total"
                                    class="box-body with-border clearfix"
                                >
                                    <BadgeTotal
                                        :label="`${$t('total')}: `"
                                        :value="paginationOptions.total"
                                        color="gray"
                                    />

                                    <div
                                        v-if="paginationOptions.last_page > 1"
                                        class="pagination-box"
                                    >
                                        <PaginationTable
                                            :options="paginationOptions"
                                            @change-page="fetchTags"
                                        />
                                    </div>
                                </div>

                                <div class="box-body table-responsive no-padding">
                                    <table class="table table-striped table-hover">
                                        <thead>
                                            <tr>
                                                <th>{{ $t('id') }}</th>

                                                <th>
                                                    {{ $t('name') }}
                                                </th>

                                                <th>
                                                    {{ $t('active') }}
                                                </th>

                                                <th>
                                                    {{ $t('listCategory.categories') }}
                                                </th>

                                                <th class="text-right">
                                                    {{ $t('createdAt') }}
                                                </th>

                                                <th class="text-right">
                                                    {{ $t('updatedAt') }}
                                                </th>

                                                <th></th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            <tr
                                                v-for="tag in tags"
                                                :key="tag.id"
                                            >
                                                <td>{{ tag.id }}</td>

                                                <td>{{ tag.name }}</td>

                                                <td>
                                                    <ValueYesNo :value="tag.active" />
                                                </td>

                                                <td>
                                                    <BadgeTotal
                                                        :value="tag.categoriesCount"
                                                        color="gray"
                                                    />
                                                </td>

                                                <td class="text-right">
                                                    {{ formatDate(tag.createdAt) }}
                                                </td>

                                                <td class="text-right">
                                                    {{ formatDate(tag.updatedAt) }}
                                                </td>

                                                <td class="text-right text-nowrap">
                                                    <router-link
                                                        :title="$t('show')"
                                                        :to="{
                                                            name: 'contacts-lists.categories.tags.show',
                                                            params: {
                                                                id: tag.id,
                                                            },
                                                        }"
                                                        class="btn btn-sm btn-info"
                                                        data-toggle="tooltip"
                                                    >
                                                        <i class="fa fa-eye" />
                                                        <span class="sr-only">
                                                            {{ $t('show') }}
                                                        </span>
                                                    </router-link>

                                                    <router-link
                                                        :title="$t('edit')"
                                                        :to="{
                                                            name: 'contacts-lists.categories.tags.edit',
                                                            params: {
                                                                id: tag.id,
                                                            },
                                                        }"
                                                        class="btn btn-sm btn-primary"
                                                        data-toggle="tooltip"
                                                    >
                                                        <i class="fa fa-edit" />
                                                        <span class="sr-only">
                                                            {{ $t('edit') }}
                                                        </span>
                                                    </router-link>

                                                    <button
                                                        :class="{
                                                            'btn-danger': tag.active,
                                                            'btn-success': !tag.active,
                                                        }"
                                                        :disabled="tag.loading"
                                                        :title="tag.active ? $t('makeInactive') : $t('makeActive')"
                                                        class="btn btn-sm"
                                                        type="button"
                                                        @click="toggleActive(tag)"
                                                    >
                                                        <i
                                                            v-if="tag.loading"
                                                            class="fa fa-refresh fa-spin"
                                                        />

                                                        <template v-else>
                                                            <i
                                                                v-if="tag.active"
                                                                class="fa fa-ban"
                                                            />
                                                            <i
                                                                v-else
                                                                class="fa fa-check"
                                                            />
                                                        </template>

                                                        <span class="sr-only">
                                                            {{ tag.active ? $t('makeInactive') : $t('makeActive') }}
                                                        </span>
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div
                                    v-if="paginationOptions.last_page > 1"
                                    class="box-footer clearfix"
                                >
                                    <div class="pagination-box">
                                        <PaginationTable
                                            :options="paginationOptions"
                                            @change-page="fetchTags"
                                        />
                                    </div>
                                </div>
                            </vue-slide-up-down>
                        </template>

                        <div
                            v-else
                            class="box-body with-border clearfix"
                        >
                            <p>
                                {{ $t('messages.list.category.tag.noTags') }}
                                <router-link :to="{ name: 'contacts-lists.categories.tags.create' }">
                                    {{ $t('addNew') }}
                                </router-link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import PaginationTable from '@/components/subcomponents/PaginationTable';
import BadgeTotal from '@/components/subcomponents/BadgeTotal';
import ValueYesNo from '@/components/subcomponents/ValueYesNo';
import formatsMixin from '@/mixins/formatsMixin';

export default {
    components: {
        PaginationTable,
        BadgeTotal,
        ValueYesNo,
    },
    mixins: [formatsMixin],
    data() {
        return {
            isCollapse: true,
            isLoaded: false,
            tags: [],
            paginationOptions: {
                disabled: true,
                total: 0,
            },
        };
    },
    created() {
        this.fetchTags(this.$route.query.page);
    },
    methods: {
        async fetchTags(page = 1) {
            try {
                this.$router
                    .replace({
                        query: {
                            ...this.$route.query,
                            page: page > 1 ? page : undefined,
                        },
                    })
                    .catch(() => {});

                this.paginationOptions.disabled = true;

                const {
                    data: {
                        tags: { data: tags, ...paginationOptions },
                    },
                } = await axios.get('/lists/categories/tags/data', {
                    params: {
                        page,
                    },
                });

                this.tags = tags;
                this.paginationOptions = paginationOptions;
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoaded = true;
                this.paginationOptions.disabled = false;
            }
        },
        async toggleActive(tagData) {
            try {
                this.$set(tagData, 'loading', true);

                const {
                    data: { tag },
                } = await axios.put(`/lists/categories/tags/data/${tagData.id}`, {
                    active: !tagData.active,
                });

                tagData = Object.assign(tagData, tag);
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                tagData.loading = false;
            }
        },
    },
};
</script>
